/**
 * The bar along the bottom of a phone.
 *
 * A field rep works one-handed, standing outside a shop. Reaching the top of
 * the screen for a hamburger, waiting for a drawer, scrolling a menu of two
 * hundred entries and tapping the right one is four actions to get to a
 * screen they open thirty times a day.
 *
 * So on a phone the four things they actually do sit along the bottom, under
 * the thumb. It appears only when there is something worth putting there:
 * a login with no field rights sees nothing and loses no room.
 *
 * It is not a second navigation. Every destination is a screen that is also
 * in the menu; this is a shortcut to four of them.
 */
import { NavLink, useLocation } from 'react-router-dom';
import { Banknote, Footprints, LayoutDashboard, Store } from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePermissions } from '@/stores/session';

interface Stop {
  to: string;
  label: string;
  icon: typeof Store;
  module: string;
  /** Also lit on deeper screens, e.g. one shop inside the shop list. */
  prefix?: boolean;
}

const STOPS: Stop[] = [
  { to: '/field/my-day', label: 'My day', icon: Footprints, module: 'field.visit' },
  { to: '/field/outlets', label: 'Shops', icon: Store, module: 'field.outlet', prefix: true },
  { to: '/field/orders', label: 'Orders', icon: LayoutDashboard, module: 'field.order', prefix: true },
  { to: '/field/collections', label: 'Collect', icon: Banknote, module: 'field.collection', prefix: true },
];

export function BottomBar() {
  const { canAccess } = usePermissions();
  const { pathname } = useLocation();

  const stops = STOPS.filter((s) => canAccess(s.module));
  // One shortcut is not a bar; it is just a button in the way.
  if (stops.length < 2) return null;

  return (
    <nav
      aria-label="Field shortcuts"
      className="shrink-0 border-t border-border bg-card lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <ul className="flex">
        {stops.map((s) => {
          const active = pathname === s.to || (s.prefix && pathname.startsWith(`${s.to}/`));
          const Icon = s.icon;
          return (
            <li key={s.to} className="flex-1">
              <NavLink
                to={s.to}
                className={cn(
                  'flex h-16 flex-col items-center justify-center gap-1 text-[13px] font-medium transition-colors',
                  active ? 'text-primary' : 'text-muted-foreground',
                )}
              >
                <Icon className={cn('h-6 w-6', active && 'stroke-[2.25]')} aria-hidden />
                {s.label}
              </NavLink>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
